import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { classAPI, studentAPI } from '../utils/api';
import useDebouncedValue from '../hooks/useDebouncedValue';
import '../styles/list.css';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const getGenderIconClass = (gender) => {
  if (gender === 'Male') return 'fas fa-mars';
  if (gender === 'Female') return 'fas fa-venus';
  return 'fas fa-user';
};

const escapeCsvValue = (value) => {
  const normalized = value ?? '';
  const stringValue = String(normalized).replace(/"/g, '""');
  return `"${stringValue}"`;
};

const Students = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [classOptions, setClassOptions] = useState([]);
  const [classFilter, setClassFilter] = useState('');
  const [studentStatusFilter, setStudentStatusFilter] = useState('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalStudents, setTotalStudents] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const normalizedSearch = debouncedSearch.trim();
  const isSearchReady = !normalizedSearch || normalizedSearch.length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (!isSearchReady) {
      return;
    }
    fetchStudents();
  }, [debouncedSearch, classFilter, studentStatusFilter, currentPage, pageSize]);

  const fetchClasses = async () => {
    try {
      const response = await classAPI.getAll({ includeDisabled: 1 });
      setClassOptions(response.data.data.classes || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await studentAPI.getAll({
        search: normalizedSearch,
        class: classFilter,
        studentStatus: studentStatusFilter,
        page: currentPage,
        limit: pageSize,
      });

      setStudents(response.data.data.students || []);
      setTotalPages(response.data.data.pages || 1);
      setTotalStudents(response.data.data.total || 0);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleExport = async () => {
    if (totalStudents === 0) {
      return;
    }

    setExporting(true);

    try {
      const response = await studentAPI.getAll({
        search: normalizedSearch,
        class: classFilter,
        studentStatus: studentStatusFilter,
        page: 1,
        limit: totalStudents,
      });

      const exportStudents = response.data.data.students || [];
      const header = [
        'Name',
        'Father Name',
        'Mother Name',
        'Gender',
        'Class',
        'Caste',
        'Category',
        'Mobile Number',
        'Email',
        'Address',
        'Date of Birth',
        'Date of Admission',
        'Aadhar Number',
        'Samagra ID',
        'Family ID',
        'PEN',
        'APAAR ID',
        'Scholar Number',
        'Father Aadhar Number',
        'Mother Aadhar Number',
      ];

      const rows = exportStudents.map((student) => ([
        student.name,
        student.fatherName,
        student.motherName,
        student.gender,
        student.class,
        student.caste || '',
        student.category,
        student.mobileNumber,
        student.email || '',
        student.address,
        student.dob,
        student.doa,
        student.aadharNumber || '',
        student.samagra_id || '',
        student.familyId || '',
        student.pen || '',
        student.apaarId || '',
        student.scholarNumber || '',
        student.fatherAadharNo || '',
        student.motherAadharNo || '',
      ].map(escapeCsvValue).join(',')));

      const csvContent = [header.map(escapeCsvValue).join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const statusLabel = studentStatusFilter === 'transferred' ? 'ex-students' : 'current-students';

      link.href = url;
      link.setAttribute('download', `students-${statusLabel}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting students:', error);
      alert('Failed to export students');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div className="list-page">
        <div className="list-header">
          <h1>Students Management</h1>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/students/add')}
          >
            <i className="fas fa-plus"></i> Add New Student
          </button>
        </div>

        <div className="card">
          <div className="search-bar">
            <select
              value={classFilter}
              onChange={(e) => {
                setClassFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Classes</option>
              {classOptions.map((item) => (
                <option key={item.id} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Type at least 3 characters to search by name..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
            <i className="fas fa-magnifying-glass"></i>
            <select
              value={studentStatusFilter}
              onChange={(e) => {
                setStudentStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="active">Current Students</option>
              <option value="transferred">Ex Students</option>
            </select>
          </div>

          {!isSearchReady && (
            <div className="alert alert-info">Type at least 3 characters to search.</div>
          )}

          {loading ? (
            <div className="spinner"></div>
          ) : students.length > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Father Name</th>
                      <th>Class</th>
                      <th>Mobile No</th>
                      <th>Category</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <tr key={student._id}>
                        <td>
                          <span className="name-with-gender">
                            <i
                              className={`${getGenderIconClass(student.gender)} gender-icon gender-icon-${(student.gender || '').toLowerCase()}`}
                              aria-hidden="true"
                            ></i>
                            <span>{student.name}</span>
                          </span>
                        </td>
                        <td>{student.fatherName}</td>
                        <td>{student.class}</td>
                        <td>{student.mobileNumber}</td>
                        <td>
                          <span className="badge badge-warning">{student.category}</span>
                        </td>
                        <td className='width-160'>
                          <div className="action-buttons">
                            <button
                              className="btn btn-primary"
                              onClick={() => navigate(`/students/${student._id}`)}
                            >
                              <i className="fas fa-eye"></i> View
                            </button>
                            <button
                              className="btn btn-secondary"
                              onClick={() => navigate(`/students/edit/${student._id}`)}
                            >
                              <i className="fas fa-pen"></i> Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="fee-pagination-section">
                <div className="pagination-left">
                  <label className="rows-per-page">
                    Rows per page:
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(parseInt(e.target.value, 10));
                        setCurrentPage(1);
                      }}
                      className="page-size-select"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </label>
                  <span className="page-info">Total: {totalStudents} students</span>
                </div>

                <div className="pagination-center">
                  <span className="page-indicator">
                    Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                  </span>
                </div>

                <div className="pagination-controls">
                  <button
                    className="btn btn-pagination"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    title="Previous Page"
                  >
                    <i className="fas fa-chevron-left"></i> Previous
                  </button>
                  <button
                    className="btn btn-pagination"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    title="Next Page"
                  >
                    Next <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>

              <div className="list-export-section">
                <span className="list-export-text">
                  You can export current filtered <strong>{totalStudents}</strong> students data.
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={handleExport}
                  disabled={exporting || totalStudents === 0}
                >
                  <i className="fas fa-file-csv"></i> {exporting ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
            </>
          ) : (
            <div className="alert alert-info">No students found. Add a new student to get started!</div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Students;
